import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import TwitterIcon from '@mui/icons-material/Twitter';
import { CardMedia, Container, Divider, Grid, Link, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useNonprofitContext } from '../../hooks/commonOrgContext';
import { UploadProps } from '../../hooks/useProcessImage';
import useGlobalStyles from '../../styles/globalStyles';
import useOrgStyles from '../../styles/orgStyles';
import { pathToLogoPlaceholder } from '../../util/icons';
import { resizedImageUrl } from '../../util/resizedImageUrl';
import { regionToString } from '../../util/stringUtil';
import CardMediaDropZone from '../Forms/CardMediaDropZone';
import ContentContainer from '../Global/ContentContainer';
import { StyledMarkdown } from '../Global/StyledMarkdown';
import IncompleteProfileAlertBox from './IncompleteProfileAlertBox';

export function NpoProfileSkeleton() {
  const globalStyles = useGlobalStyles()
  return (
    <main>
      <Container maxWidth="lg" className={globalStyles.heroContent}>

        <Skeleton animation="wave" variant="rectangular" height={56} />

        <Divider className={globalStyles.primaryDivider} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={400} animation="wave" />
          </Grid>

          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" animation="wave" height={125} width={300} />

            <Divider className={globalStyles.secondaryDivider} />
            <Skeleton animation="wave" variant="rectangular" height={200} />
          </Grid>
        </Grid>

        <Divider className={globalStyles.primaryDivider} />
      </Container>
    </main>
  )
}

interface Props {
  preview?: boolean,
  logoUploadProps?: UploadProps,
  bannerUploadProps?: UploadProps,
}

export default function NpoProfileHeroContent({ preview, logoUploadProps, bannerUploadProps }: Props) {
  const npoStyles = useOrgStyles();
  const { npo, canAdmin } = useNonprofitContext()

  const bannerProps = {
    component: "img",
    className: npoStyles.bannerImage,
    image: bannerUploadProps?.imageUrl || resizedImageUrl(npo?.bannerImage, 600, 400),
  }

  const logoProps = {
    title: `${npo?.name} logo`,
    component: "img",
    className: npoStyles.logoImage,
    image: logoUploadProps?.imageUrl || resizedImageUrl(npo?.logo, 300, 200) || pathToLogoPlaceholder,
  }

  if (!npo) return <NpoProfileSkeleton />

  return (
    <ContentContainer>

      <Grid container mt={4}>
        <IncompleteProfileAlertBox completeProfile={npo.hasCompleteProfile} />
        <Grid item xs={12} md={8}>
          {preview ?
            <CardMediaDropZone {...bannerProps} uploadProps={bannerUploadProps} />
            :
            <CardMedia {...bannerProps} />
          }
          {npo.extraFields?.mission &&
            <>
              <Typography variant="h4" mt={5}>Mission</Typography>
              <StyledMarkdown>{npo.extraFields?.mission ?? ""}</StyledMarkdown>
            </>}
          {npo.extraFields?.howVolunteersHelp &&
            <>
              <Typography variant="h4" mt={7}>How volunteers help</Typography>
              <StyledMarkdown>{npo.extraFields?.howVolunteersHelp}</StyledMarkdown>
            </>}
        </Grid>

        {/* spacer element */}
        <Grid item xs={0} md={1} />

        <Grid item xs={12} md={3}>
          {preview ?
            <CardMediaDropZone {...logoProps} uploadProps={logoUploadProps} />
            :
            <CardMedia {...logoProps} />
          }
          <Divider sx={{ my: 3 }} />
          <Typography color='textSecondary' mt={2}>
            {regionToString(npo.primaryRegion, true)}
          </Typography>
          {npo.extraFields?.websiteURL &&
            <Link variant="subhead3B" href={npo.extraFields?.websiteURL} target="_blank">
              Visit website
              <OpenInNewOutlinedIcon sx={{ ml: "0.75rem" }} />
            </Link>
          }
          <Stack direction="row" mt={4} spacing={3}>
            {npo.extraFields?.twitter &&
              <Link href={`https://www.twitter.com/${npo.extraFields?.twitter}`} target="_blank" sx={{ '&:hover': { color: "inherit" }}}>
                <TwitterIcon sx={{ fontSize: "1.725rem" }} />
              </Link>
            }
            {npo.extraFields?.instagram &&
              <Link href={`https://www.instagram.com/${npo.extraFields?.instagram}`} target="_blank" sx={{ '&:hover': { color: "inherit" }}}>
                <InstagramIcon sx={{ fontSize: "1.725rem" }} />
              </Link>
            }
            {npo.extraFields?.facebook &&
              <Link href={`https://www.facebook.com/${npo.extraFields?.facebook}`} target="_blank" sx={{ '&:hover': { color: "inherit" }}}>
                <FacebookIcon sx={{ fontSize: "1.725rem" }} />
              </Link>
            }
          </Stack>
          {npo.ein && <Typography variant="subtitle2" mt={2}>EIN: {npo.ein}</Typography>}
          {canAdmin() && !preview &&
            <Typography variant="body2" textAlign="center" color="textSecondary" marginY={1.5}>
              <Link component={RouterLink} to={`/nonprofits/${npo.handle}/manage/settings/profile`}>
                Edit profile
              </Link>
            </Typography>
          }
        </Grid>
      </Grid>

    </ContentContainer>
  )
}
